import React, { memo, useRef } from 'react';
import { HomeSection } from './sections/HomeSection';
import { WorksSection } from './works/WorksSection';
import { FooterBar } from './layout/FooterBar';
import { HomeLayoutShell } from './layout/HomeLayoutShell';
import { DotMatrixBackground } from './shared/DotMatrixBackground';
import type { Workflow, Project } from '../../types';
import type { PromoItem } from './promo/PromoCard';
import type { PendingSkillMeta } from '@/hooks/home/useAgentInput';
import type { WorkCard } from './works/workLayout';

interface HomePageProps {
  promoItems: PromoItem[];
  workflows: Workflow[];
  projects?: Project[];
  onAgentSend: (value: string, skill?: PendingSkillMeta) => void;
  onSelectWorkflow: (id: string) => void;
  onCreateProject: () => void;
  onDeleteWorkflow: (id: string) => void;
  onRenameWorkflow: (id: string, newTitle: string) => void;
  onViewAllCanvases?: () => void;
  /** 仅由 AppNew 用于外层容器 marginLeft 偏移（72/240），HomePage 内部无需消费 */
  sidebarCollapsed?: boolean;
  /** 点击 Bong 精选作品卡 → 打开预览页（图像 188:19 / 视频 202:501），由 AppNew 接管视图切换 */
  onOpenWork?: (card: WorkCard) => void;
}

// memo 默认浅比较：阻断 AppNew 每 4 秒 sphereFlowing 翻转导致的整树重渲染
// 前提：传入的回调均已 useCallback 稳定化（AppNew 中已处理）
const HomePageComponent: React.FC<HomePageProps> = ({
  promoItems,
  workflows,
  projects,
  onAgentSend,
  onSelectWorkflow,
  onCreateProject,
  onDeleteWorkflow,
  onRenameWorkflow,
  onViewAllCanvases,
  onOpenWork,
}) => {
  // 点阵背景底部边界 → WorksSection「图像精选」TAB 标题栏（点阵延伸到标题栏底部）
  const worksBoundaryRef = useRef<HTMLDivElement | null>(null);
  return (
    <div className="w-full min-h-screen text-zinc-100" style={{ backgroundColor: '#121212' }}>
      <main className="relative w-full">
        {/* 顶部用户栏已上提至 AppNew 层的 HomeTopBar（原生 fixed 流式，无缩放）：
            全局唯一顶栏，HomePage 内不再重复渲染 */}
        <div className="absolute inset-0 noise-bg pointer-events-none" />
        {/* oiioii 式点阵背景：鼠标周围点亮 + 拖尾；底部边界为「图像精选」TAB 标题栏
            （worksBoundaryRef 实时钳制容器高度到标题栏底部，滚动/吸顶跟随），不挡交互 */}
        <DotMatrixBackground bottomBoundaryRef={worksBoundaryRef} />
        <HomeLayoutShell>
          <HomeSection
            promoItems={promoItems}
            workflows={workflows}
            projects={projects}
            onAgentSend={onAgentSend}
            onSelectWorkflow={onSelectWorkflow}
            onCreateProject={onCreateProject}
            onDeleteWorkflow={onDeleteWorkflow}
            onRenameWorkflow={onRenameWorkflow}
            onViewAllCanvases={onViewAllCanvases}
          />
          <WorksSection worksBoundaryRef={worksBoundaryRef} onOpen={onOpenWork} />
          <FooterBar />
        </HomeLayoutShell>
      </main>
    </div>
  );
};

export const HomePage = memo(HomePageComponent);
